import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, BarChart3, ChevronDown, Loader2 } from "lucide-react";
import { setCanonical, setMetaTags, BreadcrumbSchema, FAQSchema, SpeakableSchema } from "@/components/seo/SchemaMarkup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAJINI_URL = "https://storajini.com";

const COMPARE_FAQS = [
  { question: "How do storage costs differ across Philippine cities?", answer: "Storage costs vary significantly. Metro Manila cities like Makati and Taguig tend to be most expensive (PHP 500-2,000/day) due to high demand. Provincial cities like Davao, Cebu, and Bacolod offer more affordable options (PHP 200-800/day). Peer-to-peer rentals through Storajini often provide the best value." },
  { question: "Which Philippine city has the cheapest storage?", answer: "Generally, cities outside Metro Manila like Dagupan, Laoag, and Tuguegarao offer the most affordable storage options. However, availability may be more limited. For the best balance of price and availability, consider cities like Antipolo, Bacoor, or Santa Rosa which are near Metro Manila but more affordable." },
  { question: "Is parking more expensive in Makati or BGC?", answer: "Both areas have premium parking rates, but BGC (Taguig) tends to be slightly more expensive for commercial parking due to newer infrastructure. Monthly rates range from PHP 5,000-15,000 in Makati and PHP 6,000-18,000 in BGC. Renting from local residents through Storajini can save 30-50%." },
];

const COST_DATA = {
  "manila": { storage_low: 300, storage_high: 1500, parking_low: 150, parking_high: 800, label: "Very High" },
  "quezon-city": { storage_low: 250, storage_high: 1200, parking_low: 100, parking_high: 600, label: "High" },
  "makati": { storage_low: 400, storage_high: 2000, parking_low: 200, parking_high: 1000, label: "Very High" },
  "taguig": { storage_low: 350, storage_high: 1800, parking_low: 180, parking_high: 900, label: "Very High" },
  "cebu-city": { storage_low: 200, storage_high: 1000, parking_low: 80, parking_high: 500, label: "Medium" },
  "davao-city": { storage_low: 180, storage_high: 900, parking_low: 70, parking_high: 400, label: "Medium" },
  "pasig": { storage_low: 300, storage_high: 1400, parking_low: 120, parking_high: 700, label: "High" },
  "mandaluyong": { storage_low: 280, storage_high: 1300, parking_low: 110, parking_high: 650, label: "High" },
  "paranaque": { storage_low: 250, storage_high: 1100, parking_low: 100, parking_high: 550, label: "High" },
  "caloocan": { storage_low: 200, storage_high: 900, parking_low: 80, parking_high: 450, label: "Medium-High" },
  "bacolod": { storage_low: 150, storage_high: 700, parking_low: 60, parking_high: 350, label: "Low-Medium" },
  "iloilo-city": { storage_low: 160, storage_high: 750, parking_low: 65, parking_high: 370, label: "Low-Medium" },
  "baguio": { storage_low: 200, storage_high: 850, parking_low: 80, parking_high: 450, label: "Medium" },
  "cagayan-de-oro": { storage_low: 170, storage_high: 800, parking_low: 70, parking_high: 380, label: "Medium" },
  "general-santos": { storage_low: 150, storage_high: 700, parking_low: 60, parking_high: 320, label: "Low-Medium" },
  "angeles": { storage_low: 180, storage_high: 850, parking_low: 75, parking_high: 400, label: "Medium" },
  "antipolo": { storage_low: 220, storage_high: 950, parking_low: 90, parking_high: 480, label: "Medium" },
  "bacoor": { storage_low: 200, storage_high: 900, parking_low: 80, parking_high: 430, label: "Medium" },
  "santa-rosa": { storage_low: 200, storage_high: 900, parking_low: 80, parking_high: 430, label: "Medium" },
};

const DEFAULT_COST = { storage_low: 200, storage_high: 900, parking_low: 80, parking_high: 450, label: "Medium" };

export default function ComparePage() {
  const [cities, setCities] = useState([]);
  const [city1, setCity1] = useState("");
  const [city2, setCity2] = useState("");
  const [city3, setCity3] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCanonical("/compare");
    setMetaTags({
      title: "Compare Storage & Parking Costs Across Philippine Cities | Storajini Guide",
      description: "Compare storage and parking costs across 50+ Philippine cities. Find the most affordable options for your needs.",
    });
    axios.get(`${API}/cities?limit=100`).then(r => {
      setCities(r.data.cities);
      if (r.data.cities.length >= 3) {
        setCity1(r.data.cities.find(c => c.slug === "manila")?.slug || r.data.cities[0].slug);
        setCity2(r.data.cities.find(c => c.slug === "cebu-city")?.slug || r.data.cities[1].slug);
        setCity3(r.data.cities.find(c => c.slug === "davao-city")?.slug || r.data.cities[2].slug);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const getCityData = (slug) => {
    const city = cities.find(c => c.slug === slug);
    const costs = COST_DATA[slug] || DEFAULT_COST;
    return { city, costs };
  };

  const selectedCities = [city1, city2, city3].filter(Boolean).map(getCityData);

  const breadcrumbs = [{ name: "Home", url: "/" }, { name: "Compare Cities" }];

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 text-amber-600 animate-spin" /></div>;
  }

  return (
    <div data-testid="compare-page">
      <BreadcrumbSchema items={breadcrumbs} />
      <FAQSchema faqs={COMPARE_FAQS} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-amber-900 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="w-8 h-8 text-amber-400" />
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white">Compare Cities</h1>
          </div>
          <p className="text-gray-300 text-lg max-w-2xl">Compare storage and parking costs across Philippine cities. Find the best value for your needs.</p>
        </div>
      </section>

      {/* City Selectors */}
      <section className="py-10 bg-white border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { val: city1, set: setCity1, label: "City 1" },
              { val: city2, set: setCity2, label: "City 2" },
              { val: city3, set: setCity3, label: "City 3" },
            ].map((selector, i) => (
              <div key={i} className="relative">
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">{selector.label}</label>
                <div className="relative">
                  <select
                    data-testid={`compare-city-${i + 1}`}
                    value={selector.val}
                    onChange={(e) => selector.set(e.target.value)}
                    className="w-full appearance-none px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 pr-10"
                  >
                    <option value="">Select city...</option>
                    {cities.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.name} ({c.region})</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      {selectedCities.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cost Comparison</h2>
            
            {/* DISCLAIMER */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="compare-disclaimer">
              <p className="text-sm text-amber-800">
                <strong>Disclaimer:</strong> The price ranges shown below are <strong>estimated market averages</strong> based on publicly available data and general market research in the Philippines. 
                These are NOT actual Storajini prices. Actual rental rates vary based on space type, size, location, and host. 
                For real listings and current pricing, please visit <a href="https://storajini.com/search" className="text-amber-600 underline font-semibold">Storajini.com</a>.
              </p>
            </div>

            {/* Storage Comparison */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Storage Costs (PHP/day)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {selectedCities.map(({ city, costs }, i) => city && (
                  <div key={i} className="text-center p-5 rounded-xl bg-gray-50 border border-gray-100">
                    <p className="font-bold text-gray-900 text-lg mb-1">{city.name}</p>
                    <p className="text-xs text-gray-500 mb-3">{city.region}</p>
                    <div className="text-3xl font-extrabold text-amber-600 mb-1">
                      {costs.storage_low} - {costs.storage_high}
                    </div>
                    <p className="text-xs text-gray-400">PHP per day</p>
                    <div className={`mt-3 inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      costs.label.includes("Very High") ? "bg-red-50 text-red-600" :
                      costs.label.includes("High") ? "bg-orange-50 text-orange-600" :
                      costs.label.includes("Low") ? "bg-green-50 text-green-600" :
                      "bg-amber-50 text-amber-600"
                    }`}>
                      {costs.label} Cost
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Parking Comparison */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Parking Costs (PHP/day)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {selectedCities.map(({ city, costs }, i) => city && (
                  <div key={i} className="text-center p-5 rounded-xl bg-gray-50 border border-gray-100">
                    <p className="font-bold text-gray-900 text-lg mb-1">{city.name}</p>
                    <p className="text-xs text-gray-500 mb-3">{city.region}</p>
                    <div className="text-3xl font-extrabold text-emerald-600 mb-1">
                      {costs.parking_low} - {costs.parking_high}
                    </div>
                    <p className="text-xs text-gray-400">PHP per day</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Explore These Cities</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {selectedCities.map(({ city }, i) => city && (
                  <div key={i} className="space-y-2">
                    <p className="font-semibold text-gray-900">{city.name}</p>
                    <Link to={`/storage/${city.slug}`} className="block text-sm text-amber-600 hover:underline">Storage in {city.name}</Link>
                    <Link to={`/parking/${city.slug}`} className="block text-sm text-amber-600 hover:underline">Parking in {city.name}</Link>
                    <Link to={`/moving/${city.slug}`} className="block text-sm text-amber-600 hover:underline">Moving to {city.name}</Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-12 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {COMPARE_FAQS.map((faq, i) => (
              <details key={i} className="group bg-gray-50 rounded-xl border border-gray-100" data-testid={`compare-faq-${i}`}>
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.question}</span>
                  <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-6 pb-4">
                  <p className="text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-amber-500">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Find Your Space?</h2>
          <p className="text-amber-100 mb-6">Browse verified storage and parking spaces at the best rates on Storajini.com</p>
          <a
            href={`${STORAJINI_URL}/search`}
           
           
            data-testid="compare-cta-btn"
            className="inline-flex items-center px-8 py-4 bg-white text-amber-600 font-bold rounded-lg hover:bg-gray-50 transition-colors"
          >
            Browse Spaces on Storajini <ArrowRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
}
